import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useLab } from './CreateAccount.hooks';
import CreateAccount from 'components/auth/CreateAccount/CreateAccount';
import { SubStep } from 'components/auth/CreateAccount/CreateAccount.types';
import { AuthFlowLayout } from 'components/AuthFlowLayout/AuthFlowLayout';
import { Loader } from 'components/Loader/Loader';
import { usePatient } from 'hooks/usePatient/usePatient';
import { useBookingState } from 'kb-shared/booking/hooks/useBookingState';
import { StepType } from 'kb-shared/booking/types/types';
import { bookingAnalytics } from 'kb-shared/booking/utils/bookingAnalytics';
import { BookingSearchParams } from 'kb-shared/booking/utils/bookingSearchParams';
import { getAppointmentType } from 'kb-shared/booking/utils/getters';
import { toIntOrNull } from 'kb-shared/booking/utils/validation';
import { Container, Content } from 'screens/Book/Book.styled';
import { getPageTitle } from 'screens/Book/Book.utils';
import { ProgressTracker } from 'screens/Book/components/ProgressTracker/ProgressTracker';
import { LocationStep } from 'screens/Book/steps/Location/Location';
import { TimeStep } from 'screens/Book/steps/Time/Time';
import { pageUrl, RoutesPath } from 'utilities/pageUrl';
import { useBreakpoints } from 'utilities/useBreakpoints';

import { getAuthSubStepTitle } from './CreateAccount.utils';

export const CreateAccountScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = usePatient();
  const searchParams = new URLSearchParams(location.search);
  const soleraUid = searchParams.get('solera_uid');
  const [currentSubStep, setCurrentSubStep] = useState<SubStep>(
    soleraUid != null ? 'email' : 'choose_signup_option'
  );
  const { loading, error, entities, bookingParams, calculatedStep } = useBookingState();
  const previousScreenIsLogin = searchParams.get('previous') === 'login';
  const target = searchParams.get('target');
  const labId = searchParams.get('lab_id') ?? '';
  const authSubStepTitle = getAuthSubStepTitle(currentSubStep);
  const { lab, loading: loadingLab } = useLab(parseInt(labId));
  const redirectingUserRef = useRef(false);
  const { isDesktop } = useBreakpoints();

  const bookingPageTitle = getPageTitle({
    partnerClinicSearch: bookingParams.get('partner_clinic_search') ?? '',
    selectedProduct: entities.selectedProduct || null,
    stepType: calculatedStep as StepType
  });

  // used to redirect the user after a successful login
  useEffect(() => {
    // if redirect is already in progress, do not redirect again,
    // or if user is not logged in, skip everything
    if (redirectingUserRef.current || !isLoggedIn) {
      return;
    }

    // continue booking flow if booking params are present
    if (bookingParams.containsBookingDataParams()) {
      const params = bookingParams.clone();

      // if time slot is selected, skip to insurance step
      if (bookingParams.has('time_slot_id')) {
        params.set('step', 'insurance');
        navigate(`${RoutesPath.BOOKING_BOOK}?${params}`);
      } else {
        // if no time slot is selected, keep user on time step
        navigate(`${RoutesPath.BOOKING_BOOK}?${params}`);
      }
    } else {
      // If no booking params are present, redirect to home page or target page.
      // It seems like there is some race condition where user info is not available before redirect.
      // In that case user is redirected to /login first and then to the target page
      if (target) {
        navigate(target);
      } else {
        navigate(pageUrl.dashboard());
      }
    }

    redirectingUserRef.current = true;
  }, [isLoggedIn, navigate, bookingParams, target]);

  const { selectedClinic, selectedLab, selectedProduct, selectedTimeSlot, allAppointmentTypes } =
    entities;

  const appointmentType = getAppointmentType(entities.selectedProduct);

  if (loading) return <Loader container />;

  const renderLocationStep = () => (
    <LocationStep
      onBack={() => {
        const params = new BookingSearchParams(location.search);
        params.remove('partner_clinic_search');
        navigate(`${pageUrl.createAccount()}?${params}`);
      }}
      appointmentType={appointmentType || undefined}
      selectedAppointmentTypes={allAppointmentTypes ?? []}
      partnerClinicSearch={bookingParams.get('partner_clinic_search') ?? ''}
      selectedClinic={selectedClinic}
      selectedProduct={selectedProduct}
      // @ts-expect-error Lab type mismatch between kb-shared and SelectLocationVirtual.types
      selectedLab={selectedLab}
      onSelectClinic={(clinic, inPersonAppointmentType) => {
        const params = new BookingSearchParams(location.search);
        params.merge({
          clinic_id: clinic?.id.toString() ?? '',
          step: 'time',
          appointment_id: inPersonAppointmentType?.id || bookingParams.get('appointment_id') || ''
        });
        bookingAnalytics.track.clinicSelected(clinic);
        navigate(`${pageUrl.createAccount()}?${params}`);
      }}
      onSelectLab={lab => {
        const params = new BookingSearchParams(location.search);
        // remain on the same step to allow user to select a clinic
        params.set('lab_id', lab?.id ?? '');
        bookingAnalytics.track.labSelected(lab?.id);
        navigate(`${pageUrl.createAccount()}?${params}`);
      }}
      onContactUsClick={() => {
        const params = new BookingSearchParams(location.search);
        params.set('partner_clinic_search', 'search');
        bookingAnalytics.track.partnerClinicSearchStarted();
        navigate(`${pageUrl.createAccount()}?${params}`);
      }}
    />
  );

  const renderTimeStep = () => (
    // When changing also check the <TimeStep /> component of src/screens/Book/Book.tsx
    <TimeStep
      onBack={() => navigate(-1)}
      date={bookingParams.get('date') ?? undefined}
      providerId={toIntOrNull(bookingParams.get('provider_id')) ?? undefined}
      appointmentType={appointmentType || undefined}
      selectedClinic={selectedClinic}
      selectedLab={selectedLab}
      selectedTimeSlot={selectedTimeSlot}
      onTimeSlotSelect={timeSlot => {
        const params = new BookingSearchParams(location.search);
        params.merge({
          time_slot_id: timeSlot.id,
          step: 'create_account',
          appointment_id: String(timeSlot.appointmentTypeId) ?? ''
        });
        bookingAnalytics.track.timeSlotSelected(timeSlot);
        navigate(`${pageUrl.createAccount()}?${params}`);
      }}
    />
  );

  const renderCreateAccountStep = ({
    mobile,
    asBookingStep,
    currentNavigationStep
  }: {
    mobile: boolean;
    asBookingStep: boolean;
    currentNavigationStep: string;
  }) => (
    <CreateAccount
      mobileLayout={mobile}
      lab={lab ?? null}
      createAccountSubstep={currentSubStep}
      onAuthentication={() => {
        // onAuthentication callback is called on signup, but not on login even though the name might suggest otherwise
        if (asBookingStep) {
          const params = bookingParams.clone();
          params.set('step', 'insurance');
          // TODO: this should not trigger on every flow(some users already have insurance and will skip this step)
          // should be moved to insurance step itself
          bookingAnalytics.page.enterInsuranceInformation();
          navigate(`${RoutesPath.BOOKING_BOOK}?${params}`);
        } else {
          if (target && target.startsWith('/')) {
            navigate(target);
          } else {
            navigate(pageUrl.dashboard());
          }
        }
      }}
      onChangeSubstep={(step: SubStep) => setCurrentSubStep(step)}
      signInLinkIsVisible={previousScreenIsLogin}
      currentNavigationStep={currentNavigationStep}
      isBookingFlow={isBookingFlow()}
    />
  );

  if (loading || error) return <Loader container />;

  if (isLoggedIn) return null;

  const showBookingStepTitle =
    bookingParams.containsBookingDataParams() && calculatedStep !== 'create_account';

  const isBookingFlow = (): boolean => {
    const appointmentType = new URLSearchParams(location.search).get('appointment_id');

    if (typeof appointmentType === 'string') return true;

    return false;
  };

  const formatStepForNavigation = (currentStep: string | null) => {
    if (currentStep === 'choose_signup_option') return 'Choose Signup Option';
    if (currentStep === 'email') return 'Email';
    if (currentStep === 'google_attributes') return 'Google Attributes';
    if (currentStep === 'login') return 'Login';
    if (currentStep === 'forgot_password') return 'Forgot Password';
    if (currentStep === 'verification_code') return 'Verification Code';
    return '';
  };

  const formattedCurrentStep = formatStepForNavigation(currentSubStep);

  return (
    <AuthFlowLayout
      title={showBookingStepTitle ? bookingPageTitle : authSubStepTitle}
      renderAboveTitleContent={() => (
        <>{!isDesktop && isBookingFlow() && <ProgressTracker step={formattedCurrentStep} />}</>
      )}
      renderMainContent={(mobile: boolean) => {
        if (loadingLab) return <Loader />;

        return (
          <Container>
            <Content>
              {bookingParams.containsBookingDataParams() ? (
                <>
                  {calculatedStep === 'location' && renderLocationStep()}
                  {calculatedStep === 'time' && renderTimeStep()}
                  {calculatedStep === 'create_account' &&
                    renderCreateAccountStep({
                      mobile,
                      asBookingStep: true,
                      currentNavigationStep: formattedCurrentStep
                    })}
                </>
              ) : (
                renderCreateAccountStep({
                  mobile,
                  asBookingStep: false,
                  currentNavigationStep: formattedCurrentStep
                })
              )}
            </Content>
          </Container>
        );
      }}
    />
  );
};
